import React from 'react';
import { Modal, Descriptions, Table, Typography, Space, Card, Row, Col, Divider, Tag } from 'antd';
import { FileTextOutlined, UserOutlined, ShoppingCartOutlined, DollarOutlined } from '@ant-design/icons';
import { FacturaFormValues, CuerpoDocumentoItem } from '../types/facturaTypes';
import { formatCurrency } from '../utils/facturaUtils';
import { CatalogOptions } from '../hooks/useCatalogos';
import { calculateTotals } from '../utils/facturaUtils';

const { Title, Text } = Typography;

interface FacturaResumenModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
  formValues: FacturaFormValues | null;
  cuerpoDocumento: CuerpoDocumentoItem[];
  catalogos: CatalogOptions;
}

export const FacturaResumenModal: React.FC<FacturaResumenModalProps> = ({
  visible,
  onClose,
  onConfirm,
  loading = false,
  formValues,
  cuerpoDocumento,
  catalogos
}) => {


  // Add null check for formValues
  if (!formValues) {
    return (
      <Modal
        title="Resumen de Factura"
        open={visible}
        onCancel={onClose}
        footer={null}
        width={800}
      >
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Text type="secondary">No hay datos de formulario disponibles</Text>
        </div>
      </Modal>
    );
  }

  const totals = calculateTotals(cuerpoDocumento);

  // Get catalog labels
  const getDepartamentoLabel = (code: string) => {
    const dep = catalogos.departamentos.find(d => d.value === code);
    return dep ? dep.label : code;
  };

  const getMunicipioLabel = (code: string) => {
    const mun = catalogos.municipios.find(m => m.value === code);
    return mun ? mun.label : code;
  };

  const getUnidadMedidaLabel = (code: number) => {
    const unidad = catalogos.unidadesMedida.find(u => u.value === code);
    return unidad ? unidad.label : code.toString();
  };

  const getCondicionOperacionLabel = (code: number) => {
    const condicion = catalogos.condicionesOperacion.find(c => c.value === code);
    return condicion ? condicion.label : code.toString();
  };

  const getActividadEconomicaLabel = (code: string) => {
    const actividad = catalogos.actividadesEconomicas.find(a => a.value === code);
    return actividad ? actividad.label : code;
  };

  // Columns for products table
  const columns = [
    {
      title: 'Código',
      dataIndex: 'codigo',
      key: 'codigo',
      width: 100,
    },
    {
      title: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      ellipsis: true,
    },
    {
      title: 'Cantidad',
      dataIndex: 'cantidad',
      key: 'cantidad',
      width: 80,
      align: 'center' as const,
    },
    {
      title: 'Precio Unit.',
      dataIndex: 'precioUni',
      key: 'precioUni',
      width: 100,
      align: 'right' as const,
      render: (value: number) => formatCurrency(value),
    },
    {
      title: 'Unidad',
      dataIndex: 'uniMedida',
      key: 'uniMedida',
      width: 80,
      render: (value: number) => getUnidadMedidaLabel(value),
    },
    {
      title: 'Subtotal',
      key: 'subtotal',
      width: 100,
      align: 'right' as const,
      render: (_: unknown, record: CuerpoDocumentoItem) => 
        formatCurrency(record.cantidad * record.precioUni),
    },
  ];

  return (
    <Modal
      title={
        <Space>
          <FileTextOutlined style={{ color: '#1890ff' }} />
          <Title level={4} style={{ margin: 0 }}>
            Resumen de Factura Electrónica
          </Title>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      onOk={onConfirm}
      okText="Generar Factura"
      cancelText="Cancelar"
      width={900}
      confirmLoading={loading}
      style={{ top: 20 }}
    >
      <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {/* Información del Receptor */}
        <Card 
          title={
            <Space>
              <UserOutlined />
              <span>Información del Receptor</span>
            </Space>
          }
          size="small"
          style={{ marginBottom: 16 }}
        >
          <Row gutter={[16, 8]}>
            <Col xs={24} sm={12}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="NIT">{formValues.nit}</Descriptions.Item>
                <Descriptions.Item label="NRC">{formValues.receptor_nrc || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Nombre">{formValues.receptor_nombre}</Descriptions.Item>
                <Descriptions.Item label="Teléfono">{formValues.receptor_telefono || 'N/A'}</Descriptions.Item>
              </Descriptions>
            </Col>
            <Col xs={24} sm={12}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Correo">{formValues.receptor_correo || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Departamento">{getDepartamentoLabel(formValues.receptor_departamento)}</Descriptions.Item>
                <Descriptions.Item label="Municipio">{getMunicipioLabel(formValues.receptor_municipio)}</Descriptions.Item>
                <Descriptions.Item label="Dirección">{formValues.receptor_complemento}</Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>
          
          <Divider style={{ margin: '12px 0' }} />
          
          <Row gutter={[16, 8]}>
            <Col xs={24} sm={12}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Código de Actividad">{formValues.receptor_codActividad}</Descriptions.Item>
                <Descriptions.Item label="Actividad Económica">
                  {getActividadEconomicaLabel(formValues.receptor_codActividad)}
                </Descriptions.Item>
              </Descriptions>
            </Col>
            <Col xs={24} sm={12}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="No. Documento">{formValues.receptor_numDocumento}</Descriptions.Item>
                <Descriptions.Item label="Condición de Operación">
                  <Tag color="blue">
                    {getCondicionOperacionLabel(formValues.resumen_condicionOperacion)}
                  </Tag>
                </Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>
        </Card>

        {/* Productos/Servicios */}
        <Card 
          title={
            <Space>
              <ShoppingCartOutlined />
              <span>Productos y Servicios</span>
            </Space>
          }
          size="small"
          style={{ marginBottom: 16 }}
        >
          <Table 
            columns={columns}
            dataSource={cuerpoDocumento}
            pagination={false}
            size="small"
            rowKey="key"
            scroll={{ x: 600 }}
          />
        </Card>

        {/* Resumen Financiero */}
        <Card 
          title={
            <Space>
              <DollarOutlined />
              <span>Resumen Financiero</span>
            </Space>
          }
          size="small"
        >
          <Row gutter={[16, 8]}>
            <Col xs={24} sm={12}>
              <div style={{ 
                background: '#f5f5f5', 
                padding: '12px', 
                borderRadius: '6px',
                marginBottom: '8px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <Text>Subtotal:</Text>
                  <Text strong>{formatCurrency(totals.subtotal)}</Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <Text>Descuentos:</Text>
                  <Text>{formatCurrency(totals.descuento)}</Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <Text>Venta Gravada:</Text>
                  <Text>{formatCurrency(totals.ventaGravada)}</Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <Text>IVA:</Text>
                  <Text>{formatCurrency(totals.iva)}</Text>
                </div>
                <Divider style={{ margin: '8px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text strong style={{ fontSize: '16px' }}>Total:</Text>
                  <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
                    {formatCurrency(totals.total)}
                  </Text>
                </div>
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <Descriptions column={1} size="small">
                {formValues.resumen_descuNoSuj && (
                  <Descriptions.Item label="Desc. No Sujeto">
                    {formatCurrency(formValues.resumen_descuNoSuj)}
                  </Descriptions.Item>
                )}
                {formValues.resumen_descuExenta && (
                  <Descriptions.Item label="Desc. Exenta">
                    {formatCurrency(formValues.resumen_descuExenta)}
                  </Descriptions.Item>
                )}
                {formValues.resumen_descuGravada && (
                  <Descriptions.Item label="Desc. Gravada">
                    {formatCurrency(formValues.resumen_descuGravada)}
                  </Descriptions.Item>
                )}
                {formValues.resumen_porcentajeDescuento && (
                  <Descriptions.Item label="% Descuento">
                    {formValues.resumen_porcentajeDescuento}%
                  </Descriptions.Item>
                )}
                {formValues.resumen_ivaRete1 && (
                  <Descriptions.Item label="IVA Retenido">
                    {formatCurrency(formValues.resumen_ivaRete1)}
                  </Descriptions.Item>
                )}
                {formValues.resumen_reteRenta && (
                  <Descriptions.Item label="Retención Renta">
                    {formatCurrency(formValues.resumen_reteRenta)}
                  </Descriptions.Item>
                )}
                {formValues.resumen_saldoFavor && (
                  <Descriptions.Item label="Saldo a Favor">
                    {formatCurrency(formValues.resumen_saldoFavor)}
                  </Descriptions.Item>
                )}
                {formValues.resumen_numPagoElectronico && (
                  <Descriptions.Item label="No. Pago Electrónico">
                    {formValues.resumen_numPagoElectronico}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Col>
          </Row>

          {/* Información Adicional */}
          {(formValues.extension_nombEntrega || formValues.extension_docuEntrega || 
            formValues.extension_nombRecibe || formValues.extension_docuRecibe || 
            formValues.extension_observaciones || formValues.extension_placaVehiculo) && (
            <>
              <Divider />
              <Title level={5} style={{ marginTop: 16, marginBottom: 8 }}>
                Información Adicional
              </Title>
              <Row gutter={[16, 8]}>
                <Col xs={24} sm={12}>
                  <Descriptions column={1} size="small">
                    {formValues.extension_nombEntrega && (
                      <Descriptions.Item label="Nombre Entrega">
                        {formValues.extension_nombEntrega}
                      </Descriptions.Item>
                    )}
                    {formValues.extension_docuEntrega && (
                      <Descriptions.Item label="Doc. Entrega">
                        {formValues.extension_docuEntrega}
                      </Descriptions.Item>
                    )}
                    {formValues.extension_nombRecibe && (
                      <Descriptions.Item label="Nombre Recibe">
                        {formValues.extension_nombRecibe}
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                </Col>
                <Col xs={24} sm={12}>
                  <Descriptions column={1} size="small">
                    {formValues.extension_docuRecibe && (
                      <Descriptions.Item label="Doc. Recibe">
                        {formValues.extension_docuRecibe}
                      </Descriptions.Item>
                    )}
                    {formValues.extension_placaVehiculo && (
                      <Descriptions.Item label="Placa Vehículo">
                        {formValues.extension_placaVehiculo}
                      </Descriptions.Item>
                    )}
                    {formValues.extension_observaciones && (
                      <Descriptions.Item label="Observaciones">
                        {formValues.extension_observaciones}
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                </Col>
              </Row>
            </>
          )}
        </Card>
      </div>
    </Modal>
  );
};
